import type { CalendarScope } from "../services/calendar";

const STORAGE_KEY_PREFIX = "scheduleScope";

/** Everyone lands on their personal schedule first (plan Decision 4, no role branching). */
export const DEFAULT_SCOPE: CalendarScope = "mine";

/** localStorage key for the schedule scope of a specific organization. */
const getStorageKey = (organizationId: string): string => {
  return `${STORAGE_KEY_PREFIX}_${organizationId}`;
};

/** Only "all" and "mine" are ever accepted from storage; anything else is junk. */
const isCalendarScope = (value: unknown): value is CalendarScope => {
  return value === "all" || value === "mine";
};

/**
 * Read the persisted schedule scope for an organization. Falls back to the
 * personal-first default when there is no org, no stored value, or a value that
 * isn't one of the two known scopes.
 */
export const getStoredScheduleScope = (
  organizationId: string | null,
): CalendarScope => {
  if (!organizationId) {
    return DEFAULT_SCOPE;
  }

  try {
    const stored = localStorage.getItem(getStorageKey(organizationId));
    if (isCalendarScope(stored)) {
      return stored;
    }
    return DEFAULT_SCOPE;
  } catch (error) {
    console.warn("Failed to load schedule scope from localStorage:", error);
    return DEFAULT_SCOPE;
  }
};

/**
 * Persist the schedule scope for an organization. No-ops without an org id or
 * for a value that isn't one of the two known scopes.
 */
export const saveScheduleScope = (
  organizationId: string | null,
  scope: CalendarScope,
): void => {
  if (!organizationId || !isCalendarScope(scope)) {
    return;
  }

  try {
    localStorage.setItem(getStorageKey(organizationId), scope);
  } catch (error) {
    console.warn("Failed to save schedule scope to localStorage:", error);
  }
};

/**
 * Clear ALL persisted schedule scopes (every organization). Used on logout so a
 * coach's toggle choice never leaks across users or tenants.
 */
export const clearAllScheduleScopes = (): void => {
  try {
    const keysToRemove: string[] = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(STORAGE_KEY_PREFIX)) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach((key) => localStorage.removeItem(key));
  } catch (error) {
    console.warn("Failed to clear all schedule scopes from localStorage:", error);
  }
};
